import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

import { useAppDispatch, useAppSelector } from "hooks/hooks";
import { chekVerificationOTP, currentUser, sendVerificationEmail } from "redux/features/auth/authSlice";
import {
  initState,
  nextStep,
  setFormData,
  setOtpError,
  setProfileClientData,
  setProfileServiceData,
  setTotalSteps,
  signUp,
  updateProfileClient,
  updateProfileService,
} from "redux/features/signUp/signUpSlice";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import StepProgress from "components/StepProgress/StepProgress";
import ConfirmEmail from "./ConfirmEmail";
import ProfieClientForm from "./ProfieClientForm";
import ProfileServiceForm from "./ProfileServiceForm";
import UserForm from "./UserForm";

const ModernPageSignUp: React.FC = () => {
  const { role } = useParams();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const signUpState = useAppSelector((state) => state.signUp);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isNextStep, setIsNextStep] = useState<boolean>(false);
  const [event, setEvent] = useState<Event>(new Event(""));

  const handleNextClick = () => {
    setIsNextStep(true);

    switch (signUpState.currentStep) {
      case 1: {
        setEvent(new Event("chekFormEvent"));
        break;
      }
      case 2: {
        setEvent(new Event("chekConfirmEmailEvent"));
        break;
      }
      case 3: {
        if (role === "client") {
          setEvent(new Event("chekProfileClientEvent"));
        }
        if (role === "service") {
          setEvent(new Event("chekProfileServiceEvent"));
        }
        break;
      }
      default: {
        setEvent(new Event(""));
        break;
      }
    }
  };

  const verificationEmail = async (data: { email: any; otp: any }) => {
    const chek = await dispatch(chekVerificationOTP(data));
    setIsLoading(false);

    if (chek.payload?.status === "OK") {
      dispatch(nextStep());
    } else {
      dispatch(setOtpError("Le OTP est invalide ou expiré."));
    }
  };

  const sendMail = () => {
    dispatch(sendVerificationEmail(signUpState.formData?.email));
  };

  const user_role = role;

  useEffect(() => {
    dispatch(initState());

    if (role && signUpState.roleType.includes(role)) {
      switch (role) {
        case "client":
        case "service": {
          dispatch(setTotalSteps(3));
          break;
        }
      }
    } else {
      navigate("/");
    }
  }, []);

  useEffect(() => {
    dispatch(currentUser());
  }, [signUpState.totalSteps === signUpState.currentStep]);

  useEffect(() => {
    switch (signUpState.loading) {
      case "pending": {
        setIsLoading(true);
        break;
      }
      case "succeeded": {
        setIsLoading(false);
        dispatch(currentUser());

        if (user_role && signUpState.totalSteps === signUpState.currentStep) {
          setTimeout(() => {
            switch (user_role) {
              case "client":
                navigate("/client/dashboard");
                break;
              case "service":
                navigate("/prestataire?creation_prestation");
                break;
              default:
                break;
            }
          }, 1000);
        } else {
          if (isNextStep) {
            dispatch(nextStep());
          }
        }
        break;
      }
      case "failed": {
        setIsLoading(false);
        break;
      }
    }
  }, [signUpState.loading, user_role, navigate, dispatch]);

  useEffect(() => {
    if (signUpState.currentStep === 2) {
      sendMail();
    }
  }, [signUpState.currentStep]);

  useEffect(() => {
    if (signUpState.formData && isNextStep) {
      dispatch(signUp(signUpState.formData));
    }
  }, [signUpState.formData]);

  useEffect(() => {
    if (signUpState.ProfileClientData && isNextStep) {
      dispatch(updateProfileClient(signUpState.ProfileClientData));
    }
  }, [signUpState.ProfileClientData]);

  useEffect(() => {
    if (signUpState.ProfileServiceData && isNextStep) {
      dispatch(updateProfileService(signUpState.ProfileServiceData));
    }
  }, [signUpState.ProfileServiceData]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.2,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
    },
    exit: {
      opacity: 0,
      y: -30,
      scale: 0.95,
    },
  };

  const getStepTitle = () => {
    switch (signUpState.currentStep) {
      case 1:
        return "Créer votre compte";
      case 2:
        return "Vérifier votre email";
      case 3:
        return role === "client" ? "Finaliser votre profil" : "Configurer votre service";
      default:
        return "Inscription";
    }
  };

  const getStepSubtitle = () => {
    switch (signUpState.currentStep) {
      case 1:
        return "Commençons par les informations de base";
      case 2:
        return "Un code de vérification a été envoyé à votre adresse email";
      case 3:
        return role === "client"
          ? "Quelques informations pour personnaliser votre expérience"
          : "Configurez votre profil prestataire";
      default:
        return "";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50/30">
      <Helmet>
        <title>Inscription - EventToBee | Location d'espaces événementiels</title>
        <meta
          name="description"
          content="Inscrivez-vous sur EventToBee pour accéder aux meilleurs espaces événementiels en Île-de-France"
        />
      </Helmet>

      <motion.div
        className="container mx-auto px-4 py-8 lg:py-12"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Progress indicator */}
        <motion.div variants={cardVariants} transition={{ duration: 0.5, ease: "easeOut" }}>
          <StepProgress
            currentStep={signUpState.currentStep}
            totalSteps={signUpState.totalSteps}
            userRole={role}
          />
        </motion.div>

        {/* Main content */}
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <motion.div
            className="text-center mb-8"
            variants={cardVariants}
            transition={{ duration: 0.5, ease: "easeOut" }}
          >
            <motion.h1
              className="text-3xl lg:text-4xl font-bold text-gray-900 mb-3"
              key={signUpState.currentStep}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              {getStepTitle()}
            </motion.h1>
            <motion.p
              className="text-lg text-gray-600 max-w-lg mx-auto"
              key={`subtitle-${signUpState.currentStep}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {getStepSubtitle()}
            </motion.p>
          </motion.div>

          {/* Form container with glassmorphism effect */}
          <motion.div className="relative" variants={cardVariants} transition={{ duration: 0.5, ease: "easeOut" }}>
            {/* Background blur effect */}
            <div className="absolute inset-0 bg-white/70 backdrop-blur-xl rounded-3xl shadow-2xl shadow-blue-500/10 border border-white/20" />

            {/* Form content */}
            <div className="relative z-10 p-8 lg:p-12">
              <AnimatePresence mode="wait">
                <motion.div
                  key={signUpState.currentStep}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.4, ease: "easeInOut" }}
                >
                  {signUpState.currentStep === 1 && (
                    <UserForm
                      className="space-y-6"
                      isLoading={isLoading}
                      role={role ?? ""}
                      handleForm={event}
                      FormData={(data) => dispatch(setFormData(data))}
                      errorUserForm={signUpState.signUpError}
                    />
                  )}

                  {signUpState.currentStep === 2 && (
                    <ConfirmEmail
                      className="space-y-6"
                      email={signUpState.formData?.email ?? ""}
                      handleForm={event}
                      FormData={(data) => verificationEmail(data)}
                      errorForm={signUpState.otpError}
                      ResendMail={sendMail}
                    />
                  )}

                  {signUpState.currentStep === 3 && role === "client" && (
                    <ProfieClientForm
                      className="space-y-6"
                      isLoading={isLoading}
                      handleForm={event}
                      FormData={(data) => dispatch(setProfileClientData(data))}
                      errorProfileForm={signUpState.signUpError}
                    />
                  )}

                  {signUpState.currentStep === 3 && role === "service" && (
                    <ProfileServiceForm
                      className="space-y-6"
                      isLoading={isLoading}
                      handleForm={event}
                      FormData={(data) => dispatch(setProfileServiceData(data))}
                      errorServiceForm={signUpState.signUpError}
                    />
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>

          {/* Action button */}
          <motion.div
            className="mt-8 text-center"
            variants={cardVariants}
            transition={{ duration: 0.5, ease: "easeOut" }}
          >
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <ButtonPrimary
                onClick={handleNextClick}
                loading={isLoading}
                disabled={isLoading}
                className="w-full sm:w-auto min-w-48 py-4 px-8 text-lg font-semibold rounded-2xl bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg shadow-blue-500/25 transform transition-all duration-300"
              >
                {signUpState.currentStep === 3 ? "✨ Finaliser mon inscription" : "Continuer →"}
              </ButtonPrimary>
            </motion.div>
          </motion.div>

          {/* Additional info */}
          {signUpState.currentStep === 1 && (
            <motion.div
              className="mt-8 text-center"
              variants={cardVariants}
              transition={{ duration: 0.5, ease: "easeOut" }}
            >
              <p className="text-sm text-gray-500">
                En vous inscrivant, vous acceptez nos{" "}
                <a href="/terms" className="text-blue-600 hover:text-blue-700 transition-colors">
                  Conditions d'utilisation
                </a>{" "}
                et notre{" "}
                <a href="/privacy" className="text-blue-600 hover:text-blue-700 transition-colors">
                  Politique de confidentialité
                </a>
              </p>
            </motion.div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ModernPageSignUp;
